import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { EinsatzPanel, kartenLink } from '../components/Einsatz';
import { KundenAuswahl } from '../components/KundenAuswahl';
import { KundeKasten, VorgangAktionen, VorgangKopf, VorgangVerlauf } from '../components/Vorgang';
import { Card, Field, Loading, Notice } from '../components/ui';
import type { BenutzerKurz, Bestellung, Kunde, Lieferung, Standort, Vorgang } from '../types';

/** Neue Lieferung - aus einer Bestellung heraus (?bestellung=) oder frei. */
export function LieferungNeuPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [kunde, setKunde] = useState<Kunde | null>(null);
  const [bestellung, setBestellung] = useState<Bestellung | null>(null);
  const [artikel, setArtikel] = useState('');
  const [leistungen, setLeistungen] = useState<string[]>([]);
  const [alleLeistungen, setAlleLeistungen] = useState<string[]>([]);
  const [andereAdresse, setAndereAdresse] = useState(false);
  const [adr, setAdr] = useState({ strasse: '', plz: '', ort: '' });
  const [hinweis, setHinweis] = useState('');
  const [standortId, setStandortId] = useState('');
  const [zustaendig, setZustaendig] = useState(user?.id ?? '');
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api<string[]>('/api/lieferungen/leistungen').then(setAlleLeistungen);
    void api<Standort[]>('/api/standorte').then(setStandorte);
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
    const bid = params.get('bestellung');
    if (bid)
      void api<Bestellung>(`/api/bestellungen/${bid}`).then(async (b) => {
        setBestellung(b);
        setArtikel(b.artikel ?? '');
        setStandortId(b.standort_id ?? '');
        setKunde((await api<{ kunde: Kunde }>(`/api/kunden/${b.kunde_id}`)).kunde);
      });
    const kid = params.get('kunde');
    if (kid && !bid) void api<{ kunde: Kunde }>(`/api/kunden/${kid}`).then((r) => setKunde(r.kunde));
  }, [params]);

  const kundeHatAdresse = !!(kunde?.strasse && kunde?.ort);

  async function anlegen() {
    if (!kunde) return;
    setBusy(true);
    setFehler('');
    try {
      const l = await api<Lieferung>('/api/lieferungen', {
        method: 'POST',
        body: {
          kundeId: kunde.id,
          bestellungId: bestellung?.id ?? null,
          artikel,
          leistungen,
          standortId: standortId || null,
          hinweis: hinweis || null,
          zustaendigId: zustaendig,
          ...(andereAdresse || !kundeHatAdresse ? adr : {}),
        },
      });
      navigate(`/lieferungen/${l.id}`);
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="kopf">
        <h1>🚚 Neue Lieferung</h1>
        {bestellung && <span className="muted">zu Bestellung {bestellung.nummer}</span>}
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="stack" style={{ maxWidth: 900 }}>
        <Card title="1. Kunde">{bestellung ? <KundenAuswahl gewaehlt={kunde} onWahl={() => undefined} /> : <KundenAuswahl gewaehlt={kunde} onWahl={setKunde} />}</Card>
        <Card title="2. Was wird geliefert?">
          <div className="stack" style={{ gap: 10 }}>
            <Field label="Artikel (Pflicht)">
              <input value={artikel} onChange={(e) => setArtikel(e.target.value)} placeholder="z. B. Waschmaschine Bosch WGG14400" />
            </Field>
            <div className="field">
              <span>Leistungen</span>
              <div className="row">
                {alleLeistungen.map((l) => (
                  <label key={l} className={leistungen.includes(l) ? 'badge info' : 'badge'} style={{ cursor: 'pointer', padding: '4px 10px' }}>
                    <input
                      type="checkbox"
                      style={{ width: 'auto', marginRight: 6 }}
                      checked={leistungen.includes(l)}
                      onChange={() => setLeistungen((alt) => (alt.includes(l) ? alt.filter((x) => x !== l) : [...alt, l]))}
                    />
                    {l}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Card>
        <Card title="3. Wohin und wer">
          <div className="stack" style={{ gap: 12 }}>
            {kundeHatAdresse && (
              <label className="row">
                <input type="checkbox" style={{ width: 'auto' }} checked={andereAdresse} onChange={(e) => setAndereAdresse(e.target.checked)} />
                An eine andere Adresse liefern als {kunde!.strasse}, {kunde!.ort}
              </label>
            )}
            {(andereAdresse || (kunde && !kundeHatAdresse)) && (
              <div className="grid3">
                <div className="span2">
                  <Field label="Straße (Pflicht)">
                    <input value={adr.strasse} onChange={(e) => setAdr({ ...adr, strasse: e.target.value })} />
                  </Field>
                </div>
                <Field label="PLZ">
                  <input value={adr.plz} onChange={(e) => setAdr({ ...adr, plz: e.target.value })} />
                </Field>
                <Field label="Ort (Pflicht)">
                  <input value={adr.ort} onChange={(e) => setAdr({ ...adr, ort: e.target.value })} />
                </Field>
              </div>
            )}
            <Field label="Hinweis für den Fahrer">
              <input value={hinweis} onChange={(e) => setHinweis(e.target.value)} placeholder="z. B. 3. OG ohne Aufzug, vorher anrufen" />
            </Field>
            <div className="grid2">
              <Field label="Standort">
                <select value={standortId} onChange={(e) => setStandortId(e.target.value)}>
                  <option value="">—</option>
                  {standorte.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Zuständig für den Termin">
                <select value={zustaendig} onChange={(e) => setZustaendig(e.target.value)}>
                  {benutzer.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        </Card>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={() => navigate(-1)}>Abbrechen</button>
          <button className="primary" disabled={busy || !kunde || !artikel.trim()} onClick={() => void anlegen()}>
            Lieferung anlegen
          </button>
        </div>
      </div>
    </>
  );
}

export function LieferungDetailPage() {
  const { id } = useParams();
  const [l, setL] = useState<Lieferung | null>(null);
  const [fehler, setFehler] = useState('');

  const laden = useCallback(() => {
    api<Lieferung>(`/api/lieferungen/${id}`)
      .then(setL)
      .catch((e) => setFehler(errorText(e)));
  }, [id]);
  useEffect(laden, [laden]);

  if (!l) return fehler ? <Notice kind="error">{fehler}</Notice> : <Loading />;
  const offen = l.status !== 'abgeschlossen' && l.status !== 'storniert';

  return (
    <>
      <VorgangKopf art="lieferung" v={l} titel={l.artikel} />
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="spalten haupt-links">
        <div className="stack">
          {offen && <EinsatzPanel einsaetze={l.einsaetze ?? []} planbar={l.status === 'erfasst'} ziel={{ lieferungId: l.id }} onNeu={laden} />}
          <Card title="Was zu tun ist">
            <VorgangAktionen
              art="lieferung"
              v={l}
              schritte={[]}
              onNeu={(v: Vorgang) => setL({ ...(v as Lieferung) })}
              offeneStatus={[{ wert: 'erfasst', label: 'Erfasst (Termin vereinbaren)' }]}
            />
            {offen && <p className="small muted" style={{ marginBottom: 0 }}>Abgeschlossen wird die Lieferung über das Einsatzprotokoll.</p>}
          </Card>
          {!offen && (l.einsaetze ?? []).length > 0 && <EinsatzPanel einsaetze={l.einsaetze ?? []} planbar={false} ziel={{ lieferungId: l.id }} onNeu={laden} />}
          <VorgangVerlauf art="lieferung" v={l} onNeu={(v) => setL(v as Lieferung)} />
        </div>
        <Card title="Angaben">
          <div className="stack" style={{ gap: 12 }}>
            <KundeKasten v={l} />
            <dl className="daten">
              <dt>Lieferadresse</dt>
              <dd>
                <a href={kartenLink(l.strasse, l.plz, l.ort)} target="_blank" rel="noreferrer">
                  {[l.strasse, [l.plz, l.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')} ↗
                </a>
              </dd>
              <dt>Leistungen</dt>
              <dd>{l.leistungen.join(', ') || '—'}</dd>
              <dt>Hinweis</dt>
              <dd>{l.hinweis ?? '—'}</dd>
              <dt>Standort</dt>
              <dd>{l.standort ?? '—'}</dd>
              <dt>Zuständig</dt>
              <dd>{l.zustaendig}</dd>
              {l.bestellung_id && (
                <>
                  <dt>Bestellung</dt>
                  <dd>
                    <Link to={`/bestellungen/${l.bestellung_id}`}>{l.bestellung_nummer}</Link>
                  </dd>
                </>
              )}
            </dl>
          </div>
        </Card>
      </div>
    </>
  );
}
